'use client';
import { useCallback, useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useUrlFilterSync } from '@/hooks/shared/useUrlFilterSync';

import { DEFAULT_FILTERS } from '@/constants';

export const useHomeFilters = () => {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Define default values for specific filters
  const defaultSort = pathname === '/new' ? 'Newest' : DEFAULT_FILTERS.SORT_BY;

  const { getParam, setParam, resetFilters } = useUrlFilterSync({
    sort: defaultSort,
    freq: DEFAULT_FILTERS.FREQUENCY,
    status: DEFAULT_FILTERS.STATUS,
    hideSport: 'false',
  });

  const sortBy = getParam('sort') || defaultSort;
  const frequency = getParam('freq') || DEFAULT_FILTERS.FREQUENCY;
  const status = getParam('status') || DEFAULT_FILTERS.STATUS;
  const selectedTag = getParam('tag');
  const hideSport = getParam('hideSport') === 'true';

  const getSortParams = useCallback((uiSort: string, currentStatus: string) => {
    if (currentStatus === 'Closed' || currentStatus === 'Resolved') {
      return { sortBy: 'closedTime', sortOrder: 'desc' as const };
    }
    switch (uiSort) {
      case '24hr Volume':
        return { sortBy: 'volume24hr', sortOrder: 'desc' as const };
      case 'Total Volume':
        return { sortBy: 'volume', sortOrder: 'desc' as const };
      case 'Liquidity':
        return { sortBy: 'liquidity', sortOrder: 'desc' as const };
      case 'Newest':
        return { sortBy: 'startDate', sortOrder: 'desc' as const };
      case 'Ending Soon':
        return { sortBy: 'endDate', sortOrder: 'asc' as const };
      default:
        return { sortBy: 'volume24hr', sortOrder: 'desc' as const };
    }
  }, []);

  const sortParams = useMemo(() => getSortParams(sortBy, status), [sortBy, status, getSortParams]);

  const handleTagClick = useCallback(
    (tagSlug: string) => {
      setParam('tag', tagSlug === 'all' ? '' : tagSlug);
    },
    [setParam],
  );

  return {
    sortBy,
    setSortBy: (val: string) => setParam('sort', val, defaultSort),
    frequency,
    setFrequency: (val: string) => setParam('freq', val, DEFAULT_FILTERS.FREQUENCY),
    status,
    setStatus: (val: string) => setParam('status', val, DEFAULT_FILTERS.STATUS),
    selectedTag,
    hideSport,
    setHideSport: (val: boolean) => setParam('hideSport', val, 'false'),
    sortParams,
    handleTagClick,
    handleResetFilters: resetFilters,
  };
};
